import { useEffect, useMemo, useState } from "react";

/**
 * One way to search a list, for every list.
 *
 * Four screens had rolled their own predicate and they had already drifted apart:
 * the employees list trimmed the query and the participant picker did not, so a code
 * pasted out of a spreadsheet — which arrives with a trailing space — matched nothing
 * there while matching fine two screens away. The picker also lowercased the query
 * again for every row it tested. None of them could find "Vikram Shah" by typing
 * "shah vikram".
 */

/** Prepared once per query, not once per row. */
export function searchTokens(query) {
  return String(query ?? "").trim().toLowerCase().split(/\s+/).filter(Boolean);
}

/** Everything a row can be found by, flattened to one lowercase haystack. */
function haystack(item, fields) {
  let s = "";
  for (const f of fields) {
    const v = typeof f === "function" ? f(item) : item[f];
    if (v !== null && v !== undefined && v !== "") s += String(v) + " ";
  }
  return s.toLowerCase();
}

/** Every token must appear somewhere, so word order does not matter. */
export function matchesTokens(item, tokens, fields) {
  if (tokens.length === 0) return true;
  const hay = haystack(item, fields);
  for (const t of tokens) if (!hay.includes(t)) return false;
  return true;
}

/**
 * Filter a list by a query.
 *
 * `fields` must be a stable reference — declare it at module scope, not inline in the
 * component, or the memo is rebuilt on every render and the work is not saved.
 */
export function useSearch(items, query, fields) {
  const tokens = useMemo(() => searchTokens(query), [query]);
  return useMemo(() => {
    if (tokens.length === 0) return items;
    return items.filter((item) => matchesTokens(item, tokens, fields));
  }, [items, tokens, fields]);
}

/**
 * For searches the SERVER runs. Typing sent a request per keystroke, so the trainings
 * screen made you press Enter instead — which nothing on screen told you, and which no
 * other search on the site needs.
 */
export function useDebouncedValue(value, delay = 300) {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return settled;
}
